import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { fetchQuery, graphql } from "react-relay";
import { useHistory, useLocation } from "react-router";

import { useFeatureIsOn } from "@growthbook/growthbook-react";
import {
	addDays,
	addMinutes,
	differenceInDays,
	differenceInMinutes,
	format,
	getDay,
	getHours,
	getMinutes,
	getWeek,
	isEqual,
	isSameDay,
	isSameMinute,
	isWithinInterval,
	parse,
	set,
	startOfDay,
	startOfHour,
	startOfMinute,
	startOfWeek,
	subDays,
} from "date-fns";
import { enUS } from "date-fns/locale";
import { noop } from "lodash";

import { useCalendarsRefetchSocket } from "~/calendar/hooks/useCalendarsRefetchSocket";
import isEventHasNoAgenda from "~/calendar/utils/isEventHasNoAgenda";
import { AppContext, ViewportContext } from "~/common";
import { useEffectAfterMount, useEffectAllDepsChange, useToggle } from "~/lib/hooks";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import store from "~/lib/store";
import { simpleHash, str2AttendeeColors } from "~/lib/utils";
import { environment, relayCache } from "~/models";
import { useNavDispatchPanel } from "~/nav";
import { CalendarObserver } from "~/nav/panels/Meetings";
import ResyncCalendar from "~/nav/panels/Meetings/ResyncCalendar";
import { type EventFilterType, type FiltersState, useEventFilters } from "~/settings/EventFilters";
import { Button, toast } from "~/ui";
import {
	type CalendarProps,
	dateFnsLocalizer,
	DateLocalizer,
	type DayLayoutFunction,
	Calendar as FullCalendar,
} from "~/ui/calendar";
import type { withDragAndDropProps } from "~/ui/calendar/addons/dragAndDrop";
// @ts-ignore
import overlap from "~/ui/calendar/utils/layout-algorithms/overlap";
import { scrollContentToEvents, scrollEventIntoView, scrollToImaginaryEvent } from "~/ui/calendar/utils/scrolling";
// @ts-ignore
import Week from "~/ui/calendar/Week";
// @ts-ignore
import WorkWeek from "~/ui/calendar/WorkWeek";

import { getTimezoneInfo } from "../settings/SettingsModal/TimezoneSelector";
import { getOccurrencesListFromRepeatAndInterval } from "../stream/utils";
import type { MeetWithUser } from "./BasePanel/MeetWith";
import CalendarShortcuts from "./CalendarShortcuts";
import type { EventFormResultEvent } from "./EventForm";
import { EventFormContext, type EventFormContextType } from "./EventForm/EventFormContext";
import useDateTimeState from "./hooks/useDateTimeState";
import useFirstWeekday from "./hooks/useFirstWeekday";
import useMoveEventMutation from "./hooks/useMoveEventMutation";
import usePreferredTimezone from "./hooks/usePreferredTimezone";
import useRecurrenceOptions from "./hooks/useRecurrenceOptions";
import partyCalendarGif from "./party-calendar.gif";
import SidePanel, {
	checkAndCloseSidePanel,
	closeSidePanel,
	SidePanelNavContext,
	type SidePanelNavContextType,
	useSidePanelState,
} from "./SidePanel";
import LeftToolbar from "./Toolbar/LeftToolbar";
import RightToolbar from "./Toolbar/RightToolbar";
import isEventEditable from "./utils/isEventEditable";
import {
	calculateDateToAvoidNoMeetingDay,
	calculateDateToAvoidShortNoticeMeeting,
	calculateSpeedyMeetingDates,
} from "./utils/meetingGuidelinesUtils";

import type { CalendarPageCalendarsQuery } from "~/__generated__/CalendarPageCalendarsQuery.graphql";
import type { CalendarPageParticipantEventsQuery } from "~/__generated__/CalendarPageParticipantEventsQuery.graphql";
import type {
	CalendarPageQuery$data,
	CalendarPageQuery as CalendarPageQueryOperation,
} from "~/__generated__/CalendarPageQuery.graphql";

import "~/ui/calendar/addons/dragAndDrop/styles.css";

import styles from "./CalendarPage.module.css";

export type CalendarPageNavState = {
	currentDate: Date;
	selectedEvent?: Event;
};

const calendarPageQuery = graphql`
	query CalendarPageQuery($end: DateTime, $start: DateTime) {
		viewer {
			events: eventsByUserEventFilters(start: $start, end: $end) {
				edges {
					node {
						id
						guid
						start
						end
						title
						isAllDay
						isOrganizer
						status
						userResponseStatus
						providerEventColor: color
						calendarColor
						fellowColor
						isHidden
						type
						conferenceCalls {
							provider
							url
						}
						calendar {
							id
							source
						}
						hasNoteContent
						hasUnreadNoteContent
						attendeePersonCount
						timeBlockType
						hasMeetingRecording
						...RecordIcon_event @relay(mask: false)
					}
				}
			}
		}
	}
`;

export const participantEventsQuery = graphql`
	query CalendarPageParticipantEventsQuery(
		$participantEmail: String!
		$participantId: ID
		$start: DateTime!
		$end: DateTime!
	) {
		viewer {
			eventsByUser(participantEmail: $participantEmail, participantId: $participantId, start: $start, end: $end) {
				edges {
					node {
						title
						start
						end
						isAllDay
						userResponseStatus
					}
				}
			}
		}
	}
`;

// TODO: this should be moved somewhere more accessible
export type Event = Omit<
	NonNullable<CalendarPageQuery$data["viewer"]["events"]["edges"][0]>["node"],
	"start" | "end" | "hasNoteContent" | "hasUnreadNoteContent" | "hasMeetingRecording" | "attendeePersonCount"
> & {
	blockType: "real" | "imaginary" | "participant" | "loading";
	start: Date;
	end: Date;
	hasNoteContent?: boolean;
	hasUnreadNoteContent?: boolean;
	hasMeetingRecording?: boolean;
	attendeePersonCount?: number;

	filteredOut?: boolean;
};

const localizer = dateFnsLocalizer({
	format,
	parse,
	startOfWeek,
	getDay,
	locales: {
		"en-US": enUS,
	},
});

export type CalendarPageContextType = {
	loadingEvents: boolean;

	getDefaultTimeState: () => {
		start: Date;
		end: Date;
		isAllDay: boolean;
		timezone?: string;
	};

	setEventToLoading: (eventGuid: string) => () => void;

	shortcutsModalOpen: boolean;
	setShortcutsModalOpen: (open: boolean) => void;
};

export type CalendarFilterContextType = {
	eventFilterOptions: EventFilterType[];
	filters: FiltersState;
	updateFilters: (newState: FiltersState) => void;

	showWeekends: boolean;
	toggleShowWeekends: () => void;

	showTileIcons: boolean;
	toggleShowTileIcons: () => void;
};

export type MeetWithContextType = {
	meetWithParticipants: MeetWithUser[];
	setMeetWithParticipants: React.Dispatch<React.SetStateAction<MeetWithUser[]>>;
	saveCurrentMeetWithParticipants: () => void;
	restorePreviousMeetWithParticipants: () => void;
};

export type HighlightsContextType = {
	noAgendaMeetingsCount: number;
	isOrganizerMeetingsCount: number;

	highlightNoAgenda: boolean;
	toggleHighlightNoAgenda: () => void;

	highlightOrganized: boolean;
	toggleHighlightOrganized: () => void;
};

export const CalendarPageContext = createContext<CalendarPageContextType>({
	loadingEvents: true,
	getDefaultTimeState: () => ({
		start: startOfMinute(new Date()),
		end: addMinutes(startOfMinute(new Date()), 15),
		isAllDay: false,
	}),
	setEventToLoading: () => noop,

	shortcutsModalOpen: false,
	setShortcutsModalOpen: noop,
});
CalendarPageContext.displayName = "CalendarPageContext";

export const MeetWithContext = React.createContext<MeetWithContextType>({
	meetWithParticipants: [],
	setMeetWithParticipants: noop,
	saveCurrentMeetWithParticipants: noop,
	restorePreviousMeetWithParticipants: noop,
});

export const CalendarFilterContext = React.createContext<CalendarFilterContextType>({
	eventFilterOptions: [],
	filters: {},
	updateFilters: noop,
	showWeekends: true,
	toggleShowWeekends: noop,
	showTileIcons: true,
	toggleShowTileIcons: noop,
});

export const HighlightContext = React.createContext<HighlightsContextType>({
	noAgendaMeetingsCount: 0,
	isOrganizerMeetingsCount: 0,
	highlightNoAgenda: false,
	toggleHighlightNoAgenda: noop,
	highlightOrganized: false,
	toggleHighlightOrganized: noop,
});

function normalizeRange(range: Date[] | { start: Date; end: Date }, localizer: DateLocalizer): [Date, Date] {
	let start: Date;
	let end: Date;
	if (Array.isArray(range)) {
		start = range[0] as Date;
		end = range[range.length - 1] as Date;
	} else {
		start = range.start;
		end = range.end;
	}
	// @ts-ignore
	return [localizer.startOf(start, "day"), localizer.endOf(end, "day")];
}

type Viewer = CalendarPageCalendarsQuery["response"]["viewer"];
export type Calendar = Viewer["calendars"]["edges"][0]["node"];
export type MeetingGuidelinesSettings = Omit<
	Viewer["account"]["extensionSettings"],
	"meetingCostEnabled" | "meetingCostEnforceWorkspaceSettings" | "meetingCostUnit" | "meetingCostAvgSalary"
> | null;

export type MeetingCostSettings = Pick<
	Viewer["account"]["extensionSettings"],
	"meetingCostEnabled" | "meetingCostEnforceWorkspaceSettings" | "meetingCostUnit" | "meetingCostAvgSalary"
> | null;
export const SHOW_WEEKENDS_STORE_KEY = "calendarShowWeekends";
const SHOW_TILE_ICONS_STORE_KEY = "calendarShowTileIcons";

function clearCalendarRelayCache() {
	for (const [key] of relayCache.entries()) {
		if (key.startsWith("CalendarPageQuery")) {
			relayCache.delete(key);
		}
	}
}

export function CalendarPage({ viewer }: { viewer: Viewer }) {
	const history = useHistory();
	const calendars = React.useMemo(() => viewer.calendars?.edges.map(edge => edge?.node) ?? [], [viewer.calendars]);
	const meetingGuidelinesSettings =
		useFeatureIsOn("extension-prompts") && viewer.account.billingAccount.hasAccessToMeetingGuidelines
			? viewer.meetingGuidelines
			: null;
	const meetingCostSettings: MeetingCostSettings =
		useFeatureIsOn("extension-prompts") && viewer.account.billingAccount.hasAccessToMeetingCost
			? {
					meetingCostEnabled: viewer.account.extensionSettings.meetingCostEnabled,
					meetingCostEnforceWorkspaceSettings: viewer.account.extensionSettings.meetingCostEnforceWorkspaceSettings,
					meetingCostUnit: viewer.account.extensionSettings.meetingCostUnit,
					meetingCostAvgSalary: viewer.account.extensionSettings.meetingCostAvgSalary,
				}
			: null;
	const { user } = React.useContext(AppContext);
	const firstWeekday = useFirstWeekday();
	const location = useLocation<CalendarPageNavState & { fromOnboarding?: boolean }>();
	const [loadingEvents, setLoadingEvents] = useState(true);
	const [hasEventsInRange, setHasEventsInRange] = useState(false);
	const [loadingParticipantEvents, setLoadingParticipantEvents] = useState(false);
	const [refetchingEvents, setRefetchingEvents] = useState(false);
	const [events, setEvents] = useState<Event[]>([]);
	const calendarScrolled = React.useRef(false);
	const [meetWithParticipants, setMeetWithParticipants] = React.useState<MeetWithUser[]>([]);
	const [savedMeetWithParticipants, setSavedMeetWithParticipants] = React.useState<MeetWithUser[]>([]);
	const [participantEvents, setParticipantEvents] = useState<{ [email: string]: Event[] }>({});
	const [eventFormTitle, setEventFormTitle] = useState<string>("");
	const [eventFormPurpose, setEventFormPurpose] = useState<string>("");
	const { workingTimezone, systemToUserTime } = usePreferredTimezone(viewer);

	const calendarTimezones = React.useMemo(() => {
		const { preferredTimezone, extraTimezones } = viewer.settings.timezonePreferences;
		type CalendarTimezone = { label: string; name: string };
		let timezones: { [key in "preferredTimezone"]: CalendarTimezone } & {
			[key in "secondTimezone" | "thirdTimezone"]?: CalendarTimezone;
		} = {
			preferredTimezone: {
				label: preferredTimezone.userLabel,
				name: getTimezoneInfo(preferredTimezone.timezone.name).name,
			},
		};

		if (extraTimezones && extraTimezones[0]) {
			timezones["secondTimezone"] = {
				label: extraTimezones[0].userLabel,
				name: getTimezoneInfo(extraTimezones[0].timezone.name).name,
			};

			if (extraTimezones[1]) {
				timezones["thirdTimezone"] = {
					label: extraTimezones[1].userLabel,
					name: getTimezoneInfo(extraTimezones[1].timezone.name).name,
				};
			}
		}

		return timezones;
	}, [viewer.settings.timezonePreferences]);

	const defaultTimeState = useCallback(() => {
		const now = startOfMinute(systemToUserTime(new Date()));
		const nextQuarterHour = addMinutes(now, 15 - (getMinutes(now) % 15));
		const startDate = nextQuarterHour;

		let guidelinesDates = calculateSpeedyMeetingDates(
			meetingGuidelinesSettings?.breaksBetweenMeetingsEnabled,
			meetingGuidelinesSettings?.breaksBetweenMeetingsBehaviour,
			startDate,
		);

		guidelinesDates = calculateDateToAvoidNoMeetingDay(
			meetingGuidelinesSettings?.noMeetingDaysEnabled,
			meetingGuidelinesSettings?.noMeetingDay,
			guidelinesDates.newStartDate,
			guidelinesDates.newEndDate,
		);

		guidelinesDates = calculateDateToAvoidShortNoticeMeeting(
			meetingGuidelinesSettings?.shortNoticeEnabled,
			meetingGuidelinesSettings?.shortNoticeNumHours,
			guidelinesDates.newStartDate,
			guidelinesDates.newEndDate,
		);

		return {
			start: guidelinesDates.newStartDate,
			end: guidelinesDates.newEndDate,
			isAllDay: false,
			timezone: workingTimezone,
		};
	}, [meetingGuidelinesSettings, workingTimezone, systemToUserTime]);
	const [eventFormTimeState, changeEventFormTime] = useDateTimeState(() => defaultTimeState());
	const {
		repeatState: eventFormRepeatState,
		customRrule: eventFormCustomRrule,
		changeRecurrence: changeEventFormRecurrence,
		repeatOptions: eventFormRepeatOptions,
		resetRecurrenceValues: resetCreateEventRecurrenceValues,
		setRecurrenceFromRrule,
	} = useRecurrenceOptions({ startDate: eventFormTimeState.start });
	const [showWeekends, toggleShowWeekends] = useToggle(!!store.get(SHOW_WEEKENDS_STORE_KEY, true));
	const [showTileIcons, toggleShowTileIcons] = useToggle(!!store.get(SHOW_TILE_ICONS_STORE_KEY));
	const moveEvent = useMoveEventMutation();
	const [highlightNoAgenda, toggleHighlightNoAgenda] = useToggle(false);
	const [highlightOrganized, toggleHighlightOrganized] = useToggle(false);
	const [noAgendaMeetingsCount, setNoAgendaMeetingsCount] = useState(0);
	const [isOrganizerMeetingsCount, setIsOrganizerMeetingsCount] = useState(0);
	const [lastEventsRange, setLastEventsRange] = useState<{ start: Date; end: Date } | null>(null);
	const [isResyncing, setIsResyncing] = useState(false);
	const [lastSynced, setLastSynced] = useState(() => {
		if (calendars.length === 0) return "";

		const unzonedLastSynced = calendars.reduce(
			(lastSynced, edge) => {
				const lastSyncDatetime = edge.lastSyncDatetime;
				if (lastSyncDatetime && (!lastSynced || new Date(lastSynced) < new Date(lastSyncDatetime))) {
					return lastSyncDatetime;
				} else {
					return lastSynced;
				}
			},
			0 as string | number,
		);

		return systemToUserTime(new Date(unzonedLastSynced)).toISOString();
	});
	const [selectNextEventOnFirstLoad, setSelectNextEventOnFirstLoad] = useState(false);
	const dispatchPanel = useNavDispatchPanel();

	const getRange = React.useMemo(() => {
		const WeekComponent = showWeekends ? Week : WorkWeek;
		return WeekComponent.range;
	}, [showWeekends]);

	const loadEventsForRange = useCallback(
		async (unzonedStart: Date, unzonedEnd: Date) => {
			setLoadingEvents(true);

			const start = systemToUserTime(unzonedStart);
			const end = systemToUserTime(unzonedEnd);
			const extendedStart = subDays(start, 7);
			const extendedEnd = addDays(end, 7);

			const response = await fetchQuery<CalendarPageQueryOperation>(
				environment,
				calendarPageQuery,
				{
					start: extendedStart.toISOString(),
					end: extendedEnd.toISOString(),
				},
				{ fetchPolicy: "network-only" },
			).toPromise();

			const responseEvents =
				response?.viewer.events.edges
					.map(edge => edge && edge.node)
					.filter((e): e is NonNullable<typeof e> => e !== null) ?? [];

			const events = responseEvents.reduce((runningEv, ev) => {
				if (ev.start === null || ev.end === null) return runningEv;
				const evStart = systemToUserTime(new Date(ev.start));
				const evEnd = systemToUserTime(new Date(ev.end));

				return [
					...runningEv,
					{
						...ev,
						blockType: "real" as const,
						title: ev.title === "" ? "(No title)" : ev.title,
						start: ev.isAllDay ? startOfDay(evStart) : evStart,
						end: ev.isAllDay ? startOfDay(evEnd) : evEnd,
					},
				];
			}, new Array<Event>());

			const participantQueries = meetWithParticipants.slice(0, 10).map(({ email, id }) =>
				fetchQuery<CalendarPageParticipantEventsQuery>(
					environment,
					participantEventsQuery,
					{
						participantEmail: email || "",
						participantId: id,
						start: extendedStart.toISOString(),
						end: extendedEnd.toISOString(),
					},
					{ fetchPolicy: "store-or-network" },
				).toPromise(),
			);

			setLoadingParticipantEvents(true);
			const participantResponses = await Promise.allSettled(participantQueries);
			const participantEvents: { [k: string]: Event[] } = participantResponses.reduce((prev, response, index) => {
				if (response.status === "fulfilled") {
					const responseEvents =
						response.value?.viewer.eventsByUser?.edges
							.map(edge => edge && edge.node)
							.filter((e): e is NonNullable<typeof e> => e != null)
							.map((e, i) => {
								const userName = meetWithParticipants[index].fullName || meetWithParticipants[index].email || "";
								const color = str2AttendeeColors(userName).ring;

								return {
									blockType: "participant",
									title: e.title || "busy",
									isAllDay: Boolean(e.isAllDay),
									start: systemToUserTime(new Date(e.start)),
									end: systemToUserTime(new Date(e.end)),
									guid: `${simpleHash(`${userName} - ${e.title} - ${e.start}`).toString()}-${i}`,
									calendarColor: `#${color}`,
									userResponseStatus: e.userResponseStatus,
									fellowColor: "CALENDAR",
								};
							}) ?? [];

					return {
						...prev,
						[meetWithParticipants[index].email || ""]: responseEvents,
					};
				} else {
					// If the promise is not fulfilled it was likely an invalid email or an email outside of the workspace
					// Ignore it.
					return prev;
				}
			}, {});

			setEvents(events);
			setParticipantEvents(participantEvents);
			setLoadingEvents(false);
			setLoadingParticipantEvents(false);
			setRefetchingEvents(false);
			setLastEventsRange({ start, end });
			updateEventsTransparencyRef.current();
		},
		[meetWithParticipants, systemToUserTime],
	);

	const processEventSaved = React.useCallback(
		(savedEvent: EventFormResultEvent) => {
			if (!savedEvent.eventGuid) {
				const foundCalendar = calendars.find(calendar => calendar.id === savedEvent.calendarId);
				setEvents(events => [
					...events,
					{
						title: savedEvent.title,
						isAllDay: savedEvent.isAllDay,
						start: savedEvent.start,
						end: savedEvent.end,
						willBeRecorded: "UNKNOWN",
						guid: "",
						id: "",
						isOrganizer: true,
						status: "confirmed",
						userResponseStatus: "accepted",
						eventColor: null,
						calendarColor: foundCalendar?.color || null,
						blockType: "real",
						isHidden: false,
						calendar: foundCalendar || {
							id: "internal",
							source: "internal",
						},
						type: "OCCURRENCE",
						conferenceCalls: [],
						timeBlockType: null,
						fellowColor: "CALENDAR",
						providerEventColor: null,
					},
				]);
			} else {
				setEvents(events => {
					return events.map(event => {
						if (event.guid === savedEvent.eventGuid) {
							return {
								...event,
								start: savedEvent.start ?? event.start,
								end: savedEvent.end ?? event.end,
								title: savedEvent.title ?? event.title,
								isAllDay: savedEvent.isAllDay ?? event.isAllDay,
							};
						}

						return event;
					});
				});
			}
		},
		[calendars],
	);

	const updateEventsTransparency = React.useCallback(() => {
		setEvents(events =>
			events.map(event => {
				const highlightsEnabled = highlightOrganized || highlightNoAgenda;
				const iOrganize = !!event.isOrganizer && !!event.attendeePersonCount && event.attendeePersonCount > 1;
				const noAgenda = isEventHasNoAgenda(event);
				let shouldHighlight = false;
				if (highlightOrganized && highlightNoAgenda) {
					shouldHighlight = iOrganize && noAgenda;
				} else if (highlightOrganized) {
					shouldHighlight = iOrganize;
				} else if (highlightNoAgenda) {
					shouldHighlight = noAgenda;
				}

				return {
					...event,
					filteredOut: highlightsEnabled && !shouldHighlight,
				};
			}),
		);
	}, [highlightNoAgenda, highlightOrganized]);

	const updateEventsTransparencyRef = React.useRef(updateEventsTransparency);

	React.useEffect(() => {
		updateEventsTransparencyRef.current = updateEventsTransparency;
		updateEventsTransparency();
	}, [updateEventsTransparency]);

	React.useEffect(() => {
		if (!lastEventsRange) return;

		let noAgendaCount = 0;
		let organizedCount = 0;
		for (const event of events) {
			if (!isWithinInterval(event.start, lastEventsRange)) continue;

			if (
				!!event.isOrganizer &&
				!!event.attendeePersonCount &&
				event.attendeePersonCount > 1 &&
				event.status !== "cancelled"
			) {
				organizedCount += 1;
			}

			if (isEventHasNoAgenda(event)) {
				noAgendaCount += 1;
			}
		}

		setNoAgendaMeetingsCount(noAgendaCount);
		setIsOrganizerMeetingsCount(organizedCount);
	}, [events, lastEventsRange]);

	const [panelNavState, panelNavStateDispatch] = useSidePanelState(() => ({
		mode: "opened",
		panelWasOpen: false,
		selectedEvent: location.state?.selectedEvent,
	}));
	const [formHasEdits, setFormHasEdits] = useState(false);
	React.useEffect(() => {
		if (panelNavState.mode === "event_form") {
			setFormHasEdits(false);
		} else {
			resetCreateEventRecurrenceValues();
			setEventFormTitle("");
		}
	}, [panelNavState.mode, resetCreateEventRecurrenceValues]);

	const updateLocationState = React.useCallback(
		(date: Date, event?: Event) => {
			history.replace({
				pathname: "/calendar",
				state: { currentDate: date, selectedEvent: event },
			});
		},
		[history],
	);

	React.useEffect(() => {
		if (!panelNavState.selectedEvent || panelNavState.mode !== "event_info") {
			return;
		}

		const selected = events.find(e => e.guid === panelNavState.selectedEvent?.guid);
		if (!selected) {
			return;
		}

		panelNavStateDispatch({ type: "event_info", event: selected });
	}, [events, panelNavState.selectedEvent, panelNavState.mode, panelNavStateDispatch]);

	const [date, setDate] = useState<Date>(location.state?.currentDate ?? systemToUserTime(new Date()));
	useEffect(() => {
		if (location.state?.currentDate) {
			setDate(location.state.currentDate);
		}
	}, [location.state?.currentDate]);

	React.useEffect(() => {
		const WeekComponent = showWeekends ? Week : WorkWeek;

		const range = WeekComponent.range(date, { localizer }) as Date[];
		const rangeStart = range[0];
		const rangeEnd = range[range.length - 1];
		const eventsInRange = events?.filter(
			e => e.start && isWithinInterval(new Date(e.start), { start: rangeStart, end: rangeEnd }),
		);
		setHasEventsInRange(eventsInRange && eventsInRange.length > 0);
	}, [events, date, showWeekends]);

	useEffectAllDepsChange(
		() => {
			if (events.length === 0 || calendarScrolled.current) {
				return;
			}

			// Should execute only on first load
			scrollContentToEvents(events, date, showWeekends, firstWeekday);
			calendarScrolled.current = true;
			if (selectNextEventOnFirstLoad) {
				setSelectNextEventOnFirstLoad(false);
				chooseEvent("NEXT");
			}
		},
		[events, loadingEvents],
		React.useLayoutEffect,
	);

	const scrollToAdjustEvents = React.useCallback(() => {
		scrollContentToEvents(events, date, showWeekends, firstWeekday);
	}, [events, date, showWeekends, firstWeekday]);

	const scrollToAdjustEventsRef = React.useRef(scrollToAdjustEvents);

	React.useEffect(() => {
		scrollToAdjustEventsRef.current = scrollToAdjustEvents;
	}, [scrollToAdjustEvents]);

	useEffectAfterMount(() => {
		scrollToAdjustEventsRef.current();
		store.set(SHOW_WEEKENDS_STORE_KEY, showWeekends);
	}, [showWeekends]);

	useEffectAfterMount(() => {
		store.set(SHOW_TILE_ICONS_STORE_KEY, showTileIcons);
	}, [showTileIcons]);

	const onRangeChange = useCallback(
		range => {
			const [start, end] = normalizeRange(range, localizer);
			loadEventsForRange(start, end);
		},
		[loadEventsForRange],
	);

	const onDateChange = useCallback(
		(newDate: Date) => {
			setDate(newDate);
			updateLocationState(newDate, panelNavState.selectedEvent);

			if (panelNavState.mode !== "event_form") {
				changeEventFormTime({ type: "SHIFT", newTime: addMinutes(startOfHour(newDate), 60) });
			}
		},
		[updateLocationState, panelNavState.selectedEvent, panelNavState.mode, changeEventFormTime],
	);

	const refetch = React.useCallback(() => {
		if (isResyncing) return;

		const range = getRange(date, { localizer });
		const [start, end] = normalizeRange(range, localizer);
		loadEventsForRange(start, end);
		setRefetchingEvents(true);
	}, [date, getRange, loadEventsForRange, isResyncing]);

	const refetchRef = React.useRef(refetch);

	React.useEffect(() => {
		refetchRef.current = refetch;
	}, [refetch]);

	useEffect(() => {
		const range = getRange(date, { localizer });
		onRangeChange(range);
	}, [date, getRange, onRangeChange]);

	const { filters, eventFilterOptions, updateFilters } = useEventFilters(viewer);
	const showCancelledRef = React.useRef(filters.includeCancelled);

	React.useEffect(() => {
		showCancelledRef.current = filters.includeCancelled;
	}, [filters.includeCancelled]);

	useEffect(() => {
		calendars.forEach(edge => {
			const id = edge.id.split("=").join("");
			if (id) {
				CalendarObserver.getInstance().addCalendar(id);
			}
		});

		CalendarObserver.getInstance().observe("Calendar", newEvents => {
			clearCalendarRelayCache();

			if (newEvents) {
				setEvents(events => {
					const filteredEvents = events.filter(event => {
						const newEvent = newEvents[event.guid];
						if (!newEvent) return true;

						if (!showCancelledRef.current && newEvent.status === "cancelled" && event.status !== "cancelled")
							return false;

						return true;
					});

					return filteredEvents.map(event => {
						const newEvent = newEvents[event.guid];
						if (!newEvent) return event;

						const newDates =
							newEvent.start && newEvent.end
								? {
										start: new Date(newEvent.start),
										end: new Date(newEvent.end),
									}
								: {
										start: event.start,
										end: event.end,
									};

						return {
							...event,
							...newEvent,
							...newDates,
						};
					});
				});
			} else {
				refetchRef?.current();
			}
		});
		return () => CalendarObserver.getInstance().unobserve("Calendar");
	}, [calendars]);

	useCalendarsRefetchSocket(user?.id || "");

	const previewEvents: Event[] = useMemo(() => {
		if (panelNavState.mode !== "event_form") {
			return [];
		}

		const range = normalizeRange(Week.range(date, { localizer }), localizer);

		const start = eventFormTimeState.start;
		const end = eventFormTimeState.end;
		const isAllDay = eventFormTimeState.isAllDay;

		const basePreviewEvent: Omit<Event, "start" | "end"> = {
			isAllDay: isAllDay,
			title: eventFormTitle || "(No title)",
			blockType: "imaginary",
			guid: "(imaginary-event-0)",
			status: "confirmed",
			userResponseStatus: "none",
			calendarColor: null,
			providerEventColor: null,
			fellowColor: "CALENDAR",
			isOrganizer: true,
			id: "",
			willBeRecorded: "UNKNOWN",
			isHidden: false,
			type: "SINGLE",
			timeBlockType: null,
			conferenceCalls: [],
			calendar: {
				id: "internal",
				source: "internal",
			},
		};

		const eventDuration = differenceInMinutes(end, start);
		const occurrences: string[] =
			eventFormRepeatState != "NEVER" && !panelNavState.editedEvent
				? getOccurrencesListFromRepeatAndInterval(
						eventFormRepeatState,
						start,
						range[0],
						range[1],
						eventFormCustomRrule ?? undefined,
					)
				: [start.toISOString()];

		return occurrences.map(occurrence => ({
			...basePreviewEvent,
			guid: `(imaginary-event-${occurrence})`,
			start: new Date(occurrence),
			end: addMinutes(new Date(occurrence), eventDuration),
		}));
	}, [
		panelNavState.mode,
		panelNavState.editedEvent,
		date,
		eventFormTimeState.start,
		eventFormTimeState.end,
		eventFormTimeState.isAllDay,
		eventFormTitle,
		eventFormRepeatState,
		eventFormCustomRrule,
	]);

	const undoFnMaker = useCallback(
		(event: Event) => async () => {
			setEvents(events => {
				return events.map(prevEvent => {
					if (prevEvent.guid === event.guid) {
						return {
							...prevEvent,
							start: event.start,
							end: event.end,
							isAllDay: event.isAllDay,
							blockType: "real",
						};
					}

					return prevEvent;
				});
			});

			dispatchAnalyticsTrack("Event move undo", { feature: "Calendar", eventGuid: event.guid });

			const undoResult = await moveEvent({
				calendarId: event.calendar.id,
				title: event.title,
				guid: event.guid,
				start: event.start,
				end: event.end,
				isAllDay: event.isAllDay,
			});

			if ("guid" in undoResult) toast.info("Undone");
			else toast.error(`Error while undoing: ${undoResult.message}`);
		},
		[moveEvent],
	);

	const rescheduleEvent = useCallback(
		async (event: Event, newTime: { start: Date; end: Date; isAllDay: boolean }) => {
			const { start, end, isAllDay: _typeUnsafeIsAllDay } = newTime;
			const isAllDay = Boolean(_typeUnsafeIsAllDay);

			if (isEqual(event.start, start) && isEqual(event.end, end) && event.isAllDay === isAllDay) return;

			const undoFn = undoFnMaker(event);

			// Place a "loading" event where the event will be moving to
			setEvents(events => {
				return events.map(prevEvent => {
					if (prevEvent.guid === event.guid) {
						return {
							...prevEvent,
							start,
							end,
							isAllDay,
							blockType: "loading",
						};
					}

					return prevEvent;
				});
			});

			const result = await moveEvent({
				calendarId: event.calendar.id,
				title: event.title,
				guid: event.guid,
				start,
				end,
				isAllDay,
			});

			// If it errored
			if ("name" in result) {
				// Move the event back
				setEvents(events => {
					return events.map(prevEvent => {
						if (prevEvent.guid === event.guid) {
							return {
								...prevEvent,
								start: event.start,
								end: event.end,
								isAllDay: event.isAllDay,
								blockType: "real",
							};
						}

						return prevEvent;
					});
				});

				toast.error(result.message);
			} else {
				setEvents(events => {
					return events.map(prevEvent => {
						if (prevEvent.guid === result.guid) {
							return {
								...prevEvent,
								start: result.start,
								end: result.end,
								isAllDay: !!result.isAllDay,
								blockType: "real",
							};
						}

						return prevEvent;
					});
				});

				toast.success(`Event saved`, {
					actions: (
						<Button size="slim" variant="secondary" onClick={undoFn}>
							Undo
						</Button>
					),
				});
			}
		},

		[undoFnMaker, moveEvent],
	);

	const setEventToLoading = useCallback((eventGuid: string) => {
		let prevEventBlockType: Event["blockType"];
		setEvents(prevEvents => {
			return prevEvents.map(prevEvent => {
				if (prevEvent.guid === eventGuid) {
					prevEventBlockType = prevEvent.blockType;
					return {
						...prevEvent,
						blockType: "loading",
					};
				}

				return prevEvent;
			});
		});

		// A function to undo the loading state in case of failure
		return () =>
			setEvents(prevEvents => {
				return prevEvents.map(prevEvent => {
					if (prevEvent.guid === eventGuid) {
						return {
							...prevEvent,
							blockType: prevEventBlockType,
						};
					}

					return prevEvent;
				});
			});
	}, []);

	const onEventDrop = useCallback<NonNullable<withDragAndDropProps<Event>["onEventDrop"]>>(
		async ({ event, ...dropSlot }) => {
			switch (event.blockType) {
				case "imaginary": {
					changeEventFormTime({ type: "SHIFT", newTime: new Date(dropSlot.start) });
					if (event.isAllDay && !dropSlot.isAllDay) {
						// dragged from all-day section
						changeEventFormTime({ type: "CHANGE_END", newTime: addMinutes(new Date(dropSlot.start), 60) });
					}
					if (Boolean(dropSlot.isAllDay) != event.isAllDay) changeEventFormTime({ type: "TOGGLE_ALL_DAY" });
					const analyticsParams = {
						eventType: "imaginary",
						...dropSlot,
					};
					dispatchAnalyticsTrack("Event dropped", { feature: "Calendar", ...analyticsParams });

					return;
				}
				case "real": {
					const { canEdit, reason } = isEventEditable(event);
					const analyticsParams = {
						eventType: "real",
						eventGuid: event.guid,
						canEdit,
						reason,
						...dropSlot,
					};
					dispatchAnalyticsTrack("Event dropped", { feature: "Calendar", ...analyticsParams });

					if (!canEdit) return toast.info(reason);

					rescheduleEvent(event, {
						start: new Date(dropSlot.start),
						end: new Date(dropSlot.end),
						isAllDay: dropSlot.isAllDay,
					});

					return;
				}
				default:
					return;
			}
		},
		[changeEventFormTime, rescheduleEvent],
	);

	const onEventResize = useCallback<NonNullable<withDragAndDropProps<Event>["onEventResize"]>>(
		async ({ event, ...newSlot }) => {
			switch (event.blockType) {
				case "imaginary": {
					const newSlotStart = new Date(newSlot.start);
					const newSlotEnd = new Date(newSlot.end);
					const eventStart = new Date(event.start);
					const eventEnd = new Date(event.end);

					const analyticsParams = {
						eventType: "imaginary",
						...newSlot,
					};

					dispatchAnalyticsTrack("Event resized", { feature: "Calendar", ...analyticsParams });

					if (newSlot.isAllDay) {
						if (!isSameDay(eventStart, newSlotStart)) {
							changeEventFormTime({
								type: "CHANGE_START",
								newTime: set(newSlotStart, {
									hours: getHours(eventStart),
									minutes: getMinutes(eventStart),
								}),
							});
						}

						if (!isSameDay(eventEnd, newSlotEnd)) {
							changeEventFormTime({
								type: "CHANGE_END",
								newTime: set(newSlotEnd, {
									hours: getHours(eventEnd),
									minutes: getMinutes(eventEnd),
								}),
							});
						}
					} else {
						if (!isSameMinute(eventStart, newSlotStart)) {
							changeEventFormTime({
								type: "CHANGE_START",
								newTime: set(eventFormTimeState.start, {
									hours: getHours(newSlotStart),
									minutes: getMinutes(newSlotStart),
								}),
							});
						}

						if (!isSameMinute(eventEnd, newSlotEnd)) {
							changeEventFormTime({
								type: "CHANGE_END",
								newTime: set(eventFormTimeState.end, {
									hours: getHours(newSlotEnd),
									minutes: getMinutes(newSlotEnd),
								}),
							});
						}
					}
					return;
				}
				case "real": {
					const { canEdit, reason } = isEventEditable(event);
					const analyticsParams = {
						eventType: "real",
						eventGuid: event.guid,
						canEdit,
						reason,
						newSlot,
					};
					dispatchAnalyticsTrack("Event resized", { feature: "Calendar", ...analyticsParams });

					if (!canEdit) return toast.info(reason);

					await rescheduleEvent(event, {
						start: new Date(newSlot.start),
						end: new Date(newSlot.end),
						isAllDay: newSlot.isAllDay,
					});
					return;
				}
				default:
					return;
			}
		},
		[changeEventFormTime, eventFormTimeState.start, eventFormTimeState.end, rescheduleEvent],
	);

	const saveCurrentMeetWithParticipants = useCallback(() => {
		setSavedMeetWithParticipants(meetWithParticipants);
	}, [meetWithParticipants]);

	const restorePreviousMeetWithParticipants = useCallback(() => {
		setMeetWithParticipants(savedMeetWithParticipants);
	}, [savedMeetWithParticipants]);

	const onSelectSlot = useCallback<NonNullable<CalendarProps<Event>["onSelectSlot"]>>(
		({ start, end, action, isAllDay }) => {
			if (action === "click") {
				if (panelNavState.mode === "event_form") {
					checkAndCloseSidePanel(formHasEdits, panelNavState, panelNavStateDispatch).then(wasClosed => {
						if (wasClosed) restorePreviousMeetWithParticipants();
					});
					return;
				} else if (panelNavState.mode === "event_info") {
					closeSidePanel(panelNavState, panelNavStateDispatch);
					return;
				}
			}

			if (isNaN(start.getTime()) || isNaN(end.getTime())) return;

			changeEventFormTime({ type: "CHANGE_START", newTime: start });
			changeEventFormTime({ type: "CHANGE_END", newTime: end });
			if (isAllDay !== eventFormTimeState.isAllDay) {
				changeEventFormTime({ type: "TOGGLE_ALL_DAY" });
			}

			if (panelNavState.mode !== "event_form") {
				saveCurrentMeetWithParticipants();
				panelNavStateDispatch({ type: "event_form" });
			}

			const analyticsParams = {
				panelAction: "event_form",
				start,
				end,
				isAllDay,
			};

			dispatchAnalyticsTrack("Slot selected", { feature: "Calendar", ...analyticsParams });
		},
		[
			formHasEdits,
			changeEventFormTime,
			panelNavState,
			panelNavStateDispatch,
			eventFormTimeState.isAllDay,
			saveCurrentMeetWithParticipants,
			restorePreviousMeetWithParticipants,
		],
	);

	const selectEvent = React.useCallback(
		(event: Event) => {
			if (event.blockType === "participant" || event.blockType === "imaginary") return false;
			panelNavStateDispatch({ type: "event_info", event });
			updateLocationState(date, event);
			return true;
		},
		[panelNavStateDispatch, date, updateLocationState],
	);

	const onSelectEvent = React.useCallback(
		(event: Event | null) => {
			if (!event) return;
			if (!selectEvent(event)) return;
			dispatchAnalyticsTrack("Event selected", { feature: "Calendar", eventGuid: event.guid });
		},
		[selectEvent],
	);

	const chooseEvent = React.useCallback(
		(direction: "PREV" | "NEXT") => {
			const sortedEvents = events.sort((a, b) => a.start.getTime() - b.start.getTime());

			const fallbackBehaviour = () => onDateChange(panelNavState.selectedEvent?.start || date);
			let nextEvent = null;

			if (panelNavState.selectedEvent) {
				const index = sortedEvents.findIndex(e => e.guid === panelNavState.selectedEvent?.guid);
				if (index === -1 || index === 0 || index === sortedEvents.length) return fallbackBehaviour();

				switch (direction) {
					case "PREV": {
						nextEvent = sortedEvents[index - 1];
						break;
					}
					case "NEXT": {
						nextEvent = sortedEvents[index + 1];
						break;
					}
				}
			} else {
				const now = startOfMinute(systemToUserTime(new Date())).getTime();

				nextEvent = sortedEvents.find(e => e.start.getTime() > now);
			}

			if (!nextEvent) return fallbackBehaviour();
			const success = selectEvent(nextEvent);

			if (!success) return fallbackBehaviour();

			const getLocalWeek = (date: Date) => getWeek(date, { weekStartsOn: firstWeekday });

			if (getLocalWeek(nextEvent.start) !== getLocalWeek(date)) {
				onDateChange(nextEvent.start);
			}

			scrollEventIntoView(nextEvent);
		},
		[events, panelNavState.selectedEvent, selectEvent, onDateChange, date, firstWeekday, systemToUserTime],
	);

	useEffect(() => {
		if (location.state?.fromOnboarding) {
			window.history.replaceState({ ...window.history.state, fromOnboarding: false }, "");
			dispatchPanel("close");
			setSelectNextEventOnFirstLoad(true);
		}
	}, [dispatchPanel, location.state?.fromOnboarding]);

	const goToNote = useCallback(
		(event: Event | null) => {
			if (event && event.blockType === "real") {
				history.push(`/meetings/${event.guid}/`);
				dispatchAnalyticsTrack("Event double click", { feature: "Calendar", eventGuid: event.guid });
			}
		},
		[history],
	);

	// TODO: Pull this into the library
	const dayLayoutAlgorithm = useCallback<DayLayoutFunction<Event>>(({ events, slotMetrics, accessors }) => {
		type EventStyle = React.CSSProperties & { xOffset: number };
		const [layerOneEvents, layerTwoEvents] = events.reduce(
			([prevLayerOne, prevLayerTwo], event) => {
				if (event.isAllDay || ["real", "participant"].includes(event.blockType)) {
					return [[...prevLayerOne, event], prevLayerTwo];
				} else {
					return [prevLayerOne, [...prevLayerTwo, event]];
				}
			},
			[new Array<Event>(), new Array<Event>()],
		);

		const layerOneEventsWithStyles: Array<{ event: Event; style: EventStyle }> = overlap({
			events: layerOneEvents,
			minimumStartDifference: 15,
			slotMetrics,
			accessors,
		});

		const layerTwoEventsWithStyles: Array<{ event: Event; style: EventStyle }> = overlap({
			events: layerTwoEvents,
			minimumStartDifference: 15,
			slotMetrics,
			accessors,
		});

		const addVerticalPadding = ({ event, style }: { event: Event; style: EventStyle }) => {
			if (typeof style.height === "number" && typeof style.top === "number") {
				return {
					event,
					style: {
						...style,
						height: `calc(${style.height}% - 2px)`,
					},
				};
			}

			return { event, style };
		};

		return [...layerOneEventsWithStyles, ...layerTwoEventsWithStyles].map(addVerticalPadding);
	}, []);

	const interactivityCondition = useCallback(
		({ blockType }: Event) => blockType === "real" || blockType === "imaginary",
		[],
	);

	const isAllDayLike = useCallback((event: Event) => {
		if (event.isAllDay) return true;

		// Some events can span multiple days but not be "all day" events
		const eventInDays = differenceInDays(event.end, event.start);
		return eventInDays > 0;
	}, []);

	const allEvents = useMemo(() => {
		const participantEventsWithoutEmails = Object.values(participantEvents).flat();
		const filteredEvents = panelNavState.editedEvent
			? events.filter(e => e.guid !== panelNavState.editedEvent?.guid)
			: events;

		return [...filteredEvents, ...participantEventsWithoutEmails, ...previewEvents];
	}, [events, participantEvents, previewEvents, panelNavState.editedEvent]);

	const onResyncStart = React.useCallback(() => {
		setIsResyncing(true);
	}, []);

	const onResyncDone = React.useCallback(() => {
		setIsResyncing(false);
		setLastSynced(systemToUserTime(new Date()).toISOString());
	}, [systemToUserTime]);

	useEffectAfterMount(() => {
		if (!isResyncing) {
			// Switched: true --> false
			refetchRef.current();
		}
	}, [isResyncing]);

	const [shortcutsModalOpen, setShortcutsModalOpen] = useState(false);
	const contextValue = React.useMemo<CalendarPageContextType>(
		() => ({
			loadingEvents: (loadingEvents && !hasEventsInRange) || loadingParticipantEvents || refetchingEvents,
			getDefaultTimeState: defaultTimeState,
			setEventToLoading,
			shortcutsModalOpen,
			setShortcutsModalOpen,
		}),
		[
			loadingEvents,
			hasEventsInRange,
			loadingParticipantEvents,
			refetchingEvents,
			defaultTimeState,
			setEventToLoading,
			shortcutsModalOpen,
			setShortcutsModalOpen,
		],
	);

	const meetWithContextValue = React.useMemo<MeetWithContextType>(
		() => ({
			meetWithParticipants,
			setMeetWithParticipants,
			saveCurrentMeetWithParticipants,
			restorePreviousMeetWithParticipants,
		}),
		[meetWithParticipants, saveCurrentMeetWithParticipants, restorePreviousMeetWithParticipants],
	);

	const filterContextValue = React.useMemo<CalendarFilterContextType>(
		() => ({
			filters,
			eventFilterOptions,
			updateFilters,
			showWeekends,
			toggleShowWeekends,
			showTileIcons,
			toggleShowTileIcons,
		}),
		[filters, eventFilterOptions, updateFilters, showWeekends, toggleShowWeekends, showTileIcons, toggleShowTileIcons],
	);

	const highlightsContextValue = React.useMemo<HighlightsContextType>(
		() => ({
			noAgendaMeetingsCount,
			isOrganizerMeetingsCount,
			highlightNoAgenda,
			toggleHighlightNoAgenda,
			highlightOrganized,
			toggleHighlightOrganized,
		}),
		[
			noAgendaMeetingsCount,
			isOrganizerMeetingsCount,
			highlightNoAgenda,
			toggleHighlightNoAgenda,
			highlightOrganized,
			toggleHighlightOrganized,
		],
	);

	const eventFormContextValue = React.useMemo<EventFormContextType>(
		() => ({
			eventTitle: eventFormTitle,
			setEventTitle: setEventFormTitle,
			eventPurpose: eventFormPurpose,
			setEventPurpose: setEventFormPurpose,
			timeState: eventFormTimeState,
			changeTime: changeEventFormTime,
			repeatState: eventFormRepeatState,
			customRrule: eventFormCustomRrule,
			changeRecurrence: changeEventFormRecurrence,
			setRecurrenceFromRrule,
			repeatOptions: eventFormRepeatOptions,
		}),
		[
			eventFormTitle,
			eventFormPurpose,
			eventFormTimeState,
			changeEventFormTime,
			eventFormRepeatState,
			eventFormCustomRrule,
			changeEventFormRecurrence,
			setRecurrenceFromRrule,
			eventFormRepeatOptions,
		],
	);

	const sidePanelNavContextValue = React.useMemo<SidePanelNavContextType>(
		() => ({
			navState: panelNavState,
			navStateDispatch: panelNavStateDispatch,
			week: date,
			setWeek: onDateChange,
			formHasEdits,
			setFormHasEdits,
		}),
		[formHasEdits, setFormHasEdits, panelNavState, panelNavStateDispatch, date, onDateChange],
	);

	const onCreateEventPressed = useCallback(() => {
		dispatchAnalyticsTrack("Create button pressed", { feature: "Calendar" });
		const { start, end, isAllDay } = defaultTimeState();
		changeEventFormTime({ type: "SET", newStart: start, newEnd: end, isAllDay });
		panelNavStateDispatch({ type: "event_form" });
		saveCurrentMeetWithParticipants();
		scrollToImaginaryEvent(250);
	}, [defaultTimeState, changeEventFormTime, panelNavStateDispatch, saveCurrentMeetWithParticipants]);

	const LeftExtra = React.useMemo(() => {
		function LeftExtraInner() {
			return <LeftToolbar openEventForm={onCreateEventPressed} />;
		}

		return LeftExtraInner;
	}, [onCreateEventPressed]);

	return (
		<CalendarPageContext.Provider value={contextValue}>
			<CalendarFilterContext.Provider value={filterContextValue}>
				<MeetWithContext.Provider value={meetWithContextValue}>
					<EventFormContext.Provider value={eventFormContextValue}>
						<HighlightContext.Provider value={highlightsContextValue}>
							<div className={styles.wrapper}>
								<SidePanelNavContext.Provider value={sidePanelNavContextValue}>
									<div className={styles.calendar}>
										<CalendarShortcuts
											chooseEvent={chooseEvent}
											isModalOpen={shortcutsModalOpen}
											setModalOpen={setShortcutsModalOpen}
										/>

										<FullCalendar<Event>
											firstWeekday={firstWeekday}
											step={15}
											timeslots={4}
											date={date}
											getNow={React.useCallback(() => systemToUserTime(new Date()), [systemToUserTime])}
											onNavigate={onDateChange}
											events={allEvents}
											showMultiDayTimes
											allDayAccessor={isAllDayLike}
											resizableCondition={interactivityCondition}
											draggableCondition={interactivityCondition}
											selectable
											onDoubleClickEvent={goToNote}
											onSelectEvent={onSelectEvent}
											onEventDrop={onEventDrop}
											onEventResize={onEventResize}
											onSelectSlot={onSelectSlot}
											dayLayoutAlgorithm={dayLayoutAlgorithm}
											onView={noop}
											view="week"
											// TODO: Make this so you don't have to import these components
											views={{
												week: showWeekends ? Week : WorkWeek,
											}}
											selected={panelNavState.selectedEvent ?? null}
											components={{
												toolbarExtras: { right: RightToolbar, left: LeftExtra },
											}}
											meetingGuidelinesSettings={meetingGuidelinesSettings}
											timezones={calendarTimezones}
										/>
									</div>

									<SidePanel
										navState={panelNavState}
										navStateDispatch={panelNavStateDispatch}
										viewer={viewer}
										onEventSaved={processEventSaved}
										meetingGuidelinesSettings={meetingGuidelinesSettings}
										meetingCostSettings={meetingCostSettings}
										hasAccessToSchedulingMeetingAssistant={viewer.hasAccessToScheduledMeetingRecordings}
									/>
								</SidePanelNavContext.Provider>

								<ResyncCalendar
									startSync={onResyncStart}
									afterSync={onResyncDone}
									currentDate={date}
									lastSynced={lastSynced}
									calendarServiceToReconnect={viewer.calendarServiceToReconnect}
								/>
							</div>
						</HighlightContext.Provider>
					</EventFormContext.Provider>
				</MeetWithContext.Provider>
			</CalendarFilterContext.Provider>
		</CalendarPageContext.Provider>
	);
}

function CalendarPageWithLazyQuery() {
	const data = useLazyLoadQuery<CalendarPageCalendarsQuery>(
		graphql`
			query CalendarPageCalendarsQuery {
				viewer {
					hasAccessToScheduledMeetingRecordings
					remainingMeetingRecordingCredits
					...EventsCalendars @relay(mask: false)
					...EventFilters_viewer
					...EventForm_viewer
					...usePreferredTimezone_viewer

					calendarServiceToReconnect

					settings {
						timezonePreferences {
							preferredTimezone {
								timezone {
									name
								}
								userLabel
							}
							extraTimezones {
								timezone {
									name
								}
								userLabel
							}
						}
					}

					meetingGuidelines {
						meetingCostEnabled
						meetingCostUnit
						meetingCostAvgSalary
						meetingAttendeesEnabled
						maxRecommendedAttendees
						attendeeWeeklyMeetingHoursWarningEnabled
						attendeeWeeklyMeetingHoursLimit
						noMeetingDaysEnabled
						noMeetingDay
						shortNoticeEnabled
						shortNoticeNumHours
						finiteRecurringMeetingsEnabled
						breaksBetweenMeetingsEnabled
						breaksBetweenMeetingsBehaviour
						promptWhenSavingEnabled
						promptWhenSavingGuidelinesUrl
					}

					account {
						extensionSettings {
							meetingCostEnabled
							meetingCostEnforceWorkspaceSettings
							meetingCostUnit
							meetingCostAvgSalary
							meetingAttendeesEnabled
							maxRecommendedAttendees
							attendeeWeeklyMeetingHoursWarningEnabled
							attendeeWeeklyMeetingHoursLimit
							noMeetingDaysEnabled
							noMeetingDay
							shortNoticeEnabled
							shortNoticeNumHours
							finiteRecurringMeetingsEnabled
							breaksBetweenMeetingsEnabled
							breaksBetweenMeetingsBehaviour
							promptWhenSavingEnabled
							promptWhenSavingGuidelinesUrl
						}
						billingAccount {
							hasAccessToMeetingGuidelines: hasAccessToFeature(feature: meeting_guidelines)
							hasAccessToMeetingCost: hasAccessToFeature(feature: meeting_cost)
							hasAccessToMeetingRecorderFeature: hasAccessToFeature(feature: meeting_recorder)
						}
					}
				}
			}
		`,
		{},
		{ fetchPolicy: "store-or-network" },
	);

	return <CalendarPage viewer={data.viewer} />;
}

function CalendarPageWithSpaceCheck() {
	const { isMobile } = useContext(ViewportContext);

	if (isMobile) {
		return (
			<div className={styles.smallScreenMessage}>
				<h1>
					Big ideas need big screens&nbsp;
					<span aria-label="*sparkling*" title="sparkles">
						✨
					</span>
				</h1>
				<p>Expand your window to see your week.</p>
				<p className={styles.partyCalendar}>
					<img
						src={partyCalendarGif}
						alt="An artistic depiction of the new Calendar experience."
						title="Your new Calendar experience, waiting to be unleashed onto a larger screen."
					/>
				</p>
			</div>
		);
	}

	return <CalendarPageWithLazyQuery />;
}

export default React.memo(CalendarPageWithSpaceCheck);
